/**
 * V66 Phase 2 — HR 휴직 관리 callable 훅
 *
 * BE callable:
 *  - createLeaveOfAbsence
 *  - executeReturnFromLeave
 *  - cancelLeaveOfAbsence
 *  - listLeaveOfAbsencesByEmployee
 */

import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';

export type LeaveOfAbsenceType = 'parental' | 'familyCare' | 'general' | 'medical';
export type LeaveOfAbsenceStatus = 'approved' | 'inProgress' | 'returned' | 'cancelled';

export const LOA_TYPE_LABELS: Record<LeaveOfAbsenceType, string> = {
  parental: '육아휴직',
  familyCare: '가족돌봄휴직',
  general: '일반휴직',
  medical: '병가/요양휴직',
};

export const LOA_STATUS_LABELS: Record<LeaveOfAbsenceStatus, string> = {
  approved: '예정',
  inProgress: '휴직중',
  returned: '복직',
  cancelled: '취소',
};

export const LOA_TYPE_MAX_LABELS: Record<LeaveOfAbsenceType, string> = {
  parental: '365일',
  familyCare: '90일',
  general: '3개월',
  medical: '6개월',
};

export interface LeaveOfAbsenceItem {
  id: string;
  employeeId: string;
  employeeName: string;
  type: LeaveOfAbsenceType;
  typeName: string;
  startDate: string;
  expectedReturnDate?: string;
  actualReturnDate?: string;
  isCurrent: boolean;
  status: LeaveOfAbsenceStatus;
  reason?: string;
  cancelReason?: string;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface UseLeaveOfAbsenceListResult {
  items: LeaveOfAbsenceItem[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * 직원별 휴직 이력 조회 hook
 */
export function useLeaveOfAbsenceList(employeeId: string | null): UseLeaveOfAbsenceListResult {
  const [items, setItems] = useState<LeaveOfAbsenceItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!employeeId) {
      setItems([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable<{ employeeId: string }, { success: boolean; items: LeaveOfAbsenceItem[] }>(
        getFunctions(),
        'listLeaveOfAbsencesByEmployee'
      );
      const res = await fn({ employeeId });
      setItems(res.data?.items || []);
    } catch (err: any) {
      console.error('[V66P2] listLeaveOfAbsencesByEmployee 실패:', err);
      setError(err?.message || '휴직 이력 조회 실패');
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { items, loading, error, refetch: fetch };
}

// ============================================
// Mutations (HR)
// ============================================

export interface CreateLoaInput {
  employeeId: string;
  type: LeaveOfAbsenceType;
  startDate: string;
  expectedReturnDate: string;
  reason?: string;
}

export async function createLeaveOfAbsence(input: CreateLoaInput): Promise<{ id: string; status: 'approved' | 'inProgress'; isCurrent: boolean }> {
  const fn = httpsCallable<CreateLoaInput, { success: boolean; id: string; status: 'approved' | 'inProgress'; isCurrent: boolean }>(
    getFunctions(),
    'createLeaveOfAbsence'
  );
  const res = await fn(input);
  if (!res.data?.success) throw new Error('휴직 등록 실패');
  return { id: res.data.id, status: res.data.status, isCurrent: res.data.isCurrent };
}

export async function executeReturnFromLeave(input: { leaveOfAbsenceId: string; actualReturnDate: string }): Promise<void> {
  const fn = httpsCallable<typeof input, { success: boolean; id: string }>(
    getFunctions(),
    'executeReturnFromLeave'
  );
  const res = await fn(input);
  if (!res.data?.success) throw new Error('복직 처리 실패');
}

export async function cancelLeaveOfAbsence(input: { leaveOfAbsenceId: string; reason?: string }): Promise<void> {
  const fn = httpsCallable<typeof input, { success: boolean; id: string }>(
    getFunctions(),
    'cancelLeaveOfAbsence'
  );
  const res = await fn(input);
  if (!res.data?.success) throw new Error('휴직 취소 실패');
}

// ============================================
// V66 Phase 4: 본인 휴직 신청
// ============================================

export interface CreateLoaRequestInput {
  type: LeaveOfAbsenceType;
  startDate: string;
  expectedReturnDate: string;
  reason?: string;
}

export interface CreateLoaRequestResult {
  approvalRequestId: string;
  approverId: string;
  approverName: string;
}

/**
 * 본인 휴직 신청 (orgAndHr 2단계 승인 — HR 최종 승인 시 leaveOfAbsences doc 생성).
 */
export async function createLeaveOfAbsenceRequest(input: CreateLoaRequestInput): Promise<CreateLoaRequestResult> {
  const fn = httpsCallable<CreateLoaRequestInput, { success: boolean; approvalRequestId: string; approverId: string; approverName: string }>(
    getFunctions(),
    'createLeaveOfAbsenceRequest'
  );
  const res = await fn(input);
  if (!res.data?.success) throw new Error('휴직 신청 실패');
  return {
    approvalRequestId: res.data.approvalRequestId,
    approverId: res.data.approverId,
    approverName: res.data.approverName,
  };
}

// ============================================
// Helpers
// ============================================

/**
 * 휴직 종류별 expectedReturnDate 의 최대 허용일 계산 (BE 와 동일 정책).
 */
export function calcMaxExpectedReturnDate(type: LeaveOfAbsenceType, startDate: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate)) return '';
  const [y, m, d] = startDate.split('-').map(Number);
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
  const fmt = (dt: Date) =>
    `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
  // V67: parental/familyCare 도 inclusive 정책에 맞춰 −1 day 보정 (BE leaveOfAbsenceCore.maxExpectedReturnDate 와 일관)
  if (type === 'parental') return fmt(new Date(Date.UTC(y, m - 1, d + 364)));
  if (type === 'familyCare') return fmt(new Date(Date.UTC(y, m - 1, d + 89)));
  if (type === 'general') return fmt(new Date(Date.UTC(y, m - 1 + 3, d - 1)));
  if (type === 'medical') return fmt(new Date(Date.UTC(y, m - 1 + 6, d - 1)));
  return '';
}
